import type { MetricType } from 'features/charts-v2/types'
import { isRidgelineMetric } from 'features/charts-v2/utils/ridgeline-bins'

/**
 * A ridgeline artifact's wire rows are (breakdown?, bin) cells whose values
 * are `[count, avg]` — count first — so the metrics read off the wire are
 * never the single score metric the strategy needs as `selectedMetrics[0]`.
 * Prefer the panel's own metric; fall back to the score metric among the wire
 * metrics. With neither, the wire metrics pass through and the strategy
 * reports the unsupported metric.
 */
export function resolveRidgelineMetrics(
  panelMetrics: readonly MetricType[] | undefined,
  wireMetrics: MetricType[],
): MetricType[] {
  const score =
    panelMetrics?.find(isRidgelineMetric) ?? wireMetrics.find(isRidgelineMetric)
  return score ? [score] : wireMetrics
}

/**
 * A ridgeline's bin domain is always the LAST domain, after the optional
 * breakdown. It is not a breakdown: mapped as one it becomes a phantom
 * "sentiment" / "rating" breakdown and an extra CSV column.
 */
export function dropRidgelineBinDomain<T>(domains: T[]): T[] {
  return domains.slice(0, -1)
}

/**
 * The number of ridges drawn, which sizes the chart. Rows are cells, so
 * counting them overstates the height; ridges are the distinct breakdown
 * values, or one when there is no breakdown.
 */
export function countRidgelineRidges(
  rows: Array<{ category?: unknown[] }>,
): number {
  if (rows.length === 0) return 0
  if (!rows.some((row) => (row.category?.length ?? 0) > 1)) return 1
  return new Set(rows.map((row) => JSON.stringify(row.category?.[0] ?? null)))
    .size
}
